import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HEADER_HEIGHT } from '../../../../../corelogic/domain/constants/headerHeight.constant';
import { UserProfile } from '../../../../../corelogic/domain/models/user.model.js';
import { safeRenderName } from '../../../../../corelogic/domain/utils/xssProtection.utils';
import { signOut } from '../../../../../corelogic/state/authentication/authentication.action';
import { store } from '../../../../../corelogic/state/store';
import { useAuthentication } from '../../hooks/useAuthentication';

const StyledNav = styled('nav')({
  position: 'fixed',
  width: '100%',
  top: 0,
  height: HEADER_HEIGHT,
  zIndex: 1000,
  background: 'rgba(0, 0, 0, 0.85)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '10px 0',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background:
      'linear-gradient(to bottom, rgba(128, 128, 128, 0.5), rgba(0, 0, 0, 0))',
  },
});

const NavLogo = styled('img')({
  height: '100%',
  marginRight: '12px',
  paddingLeft: '4px',
  cursor: 'pointer',
});

const NavList = styled('ul')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  listStyle: 'none',
  gap: '40px',
  '& li': {
    margin: '0 10px',
    position: 'relative',
    '&:last-child': {
      marginLeft: 'auto',
    },
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#fff',
  marginLeft: '4px',
  marginRight: '4px',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
}));

const StyledDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '200px',
    backgroundColor: '#000000',
    color: '#fff',
    '& .MuiDivider-root': {
      backgroundColor: '#cc0000',
      opacity: 0.5,
    },
  },
});

const DrawerListItem = styled(ListItem)<{ active?: boolean }>(({ active }) => ({
  padding: '12px 16px',
  position: 'relative',
  backgroundColor: active ? 'rgba(204, 0, 0, 0.15)' : 'transparent',
  borderLeft: active ? '4px solid #cc0000' : '4px solid transparent',
  '&:hover': {
    backgroundColor: active
      ? 'rgba(204, 0, 0, 0.2)'
      : 'rgba(255, 255, 255, 0.05)',
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.95rem',
    fontWeight: active ? 600 : 500,
    color: '#fff',
  },
}));

// New styled component for the avatar
const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 38,
  height: 38,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'all 0.2s ease',
  backgroundColor: '#8B0000',
  color: 'rgba(231, 218, 218, 0.76)',
  '&:hover': {
    boxShadow: '0 0 8px rgba(255, 255, 255, 0.5)',
    transform: 'scale(1.05)',
  },
}));

// Styled menu component
const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    backgroundColor: '#000000',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
    borderRadius: '8px',
    minWidth: '200px',
    overflow: 'hidden',
  },
  '& .MuiMenuItem-root': {
    color: 'white',
    padding: '12px 16px',
    '&:hover': {
      backgroundColor: 'rgba(204, 0, 0, 0.1)',
    },
  },
  '& .MuiDivider-root': {
    backgroundColor: '#cc0000',
    opacity: 0.5,
    margin: '4px 0',
  },
  '& .MuiListItemIcon-root': {
    color: '#cc0000',
    minWidth: '40px',
  },
});

const StyledLink = styled(Link, {
  // Filter out isActive prop from being forwarded to DOM
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive }) => ({
  color: '#fff',
  textDecoration: 'none',
  fontWeight: 'bold',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    width: isActive ? '100%' : '0%',
    height: '2px',
    backgroundColor: '#ffffff',
    transform: 'translateX(-50%)',
    transition: 'width 0.3s ease-in-out',
  },
  '&:hover::after': {
    width: '100%',
  },
}));

interface HeaderProps {
  user: UserProfile | null;
}

const Header = ({ user }: HeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthentication();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleSignOut = () => store.dispatch(signOut());

  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const open = Boolean(anchorEl);

  // effect to close menu when route changes or on authentication change
  useEffect(() => {
    setAnchorEl(null);
    setMobileOpen(false);
  }, [location.pathname, isAuthenticated]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Generate avatar initials from user name
  const getInitials = (): string => {
    if (!user) return '?';
    if (user.firstName && user.lastName) {
      const safeFirstName = safeRenderName(user.firstName);
      const safeLastName = safeRenderName(user.lastName);
      return `${safeFirstName[0] || ''}${safeLastName[0] || ''}`.toUpperCase();
    }
    if (user.username) {
      const safeUsername = safeRenderName(user.username);
      return safeUsername.substring(0, 2).toUpperCase();
    }
    return '?';
  };

  const drawer = (
    <Box sx={{ mt: 2 }}>
      <List>
        <DrawerListItem
          onClick={() => handleNavigation('/')}
          active={location.pathname === '/'}
        >
          <ListItemText primary="Home" />
        </DrawerListItem>
        <DrawerListItem
          onClick={() => handleNavigation('/hub')}
          active={location.pathname === '/hub'}
        >
          <ListItemText primary="Hub" />
        </DrawerListItem>
        {isAuthenticated && (
          <>
            <DrawerListItem
              onClick={() => handleNavigation('/teamselector')}
              active={location.pathname === '/teamselector'}
            >
              <ListItemText primary="Team Selector" />
            </DrawerListItem>
            <DrawerListItem
              onClick={() => handleNavigation('/teamviewer')}
              active={location.pathname === '/teamviewer'}
            >
              <ListItemText primary="Team Viewer" />
            </DrawerListItem>
            <DrawerListItem
              onClick={() => handleNavigation('/tournaments')}
              active={location.pathname === '/tournaments'}
            >
              <ListItemText primary="Tournaments" />
            </DrawerListItem>
            <Divider sx={{ my: 2 }} />
            <DrawerListItem
              onClick={() => handleNavigation('/profile')}
              active={location.pathname === '/profile'}
            >
              <ListItemIcon sx={{ color: '#cc0000' }}>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </DrawerListItem>
            <DrawerListItem
              onClick={() => handleNavigation('/settings')}
              active={location.pathname === '/settings'}
            >
              <ListItemIcon sx={{ color: '#cc0000' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </DrawerListItem>
            <DrawerListItem onClick={handleSignOut}>
              <ListItemIcon sx={{ color: '#cc0000' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </DrawerListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <StyledNav>
        {isMobile && (
          <MobileMenuButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </MobileMenuButton>
        )}
        <NavLogo
          src="/fantasy_mma_logo.png"
          alt="app_logo"
          onClick={() => navigate('/')}
        />
        <NavList>
          <li>
            <StyledLink
              to="/"
              state={{ nextPath: '/' }}
              isActive={location.pathname === '/'}
            >
              Home
            </StyledLink>
          </li>
          <li>
            <StyledLink
              to="/hub"
              state={{ nextPath: '/hub' }}
              isActive={location.pathname === '/hub'}
            >
              Hub
            </StyledLink>
          </li>
          {isAuthenticated && (
            <>
              <li>
                <StyledLink
                  to="/teamselector"
                  state={{ nextPath: '/teamselector' }}
                  isActive={location.pathname === '/teamselector'}
                >
                  Team Selector
                </StyledLink>
              </li>
              <li>
                <StyledLink
                  to="/teamviewer"
                  state={{ nextPath: '/teamviewer' }}
                  isActive={location.pathname === '/teamviewer'}
                >
                  Team Viewer
                </StyledLink>
              </li>
              <li>
                <StyledLink
                  to="/tournaments"
                  state={{ nextPath: '/tournaments' }}
                  isActive={location.pathname === '/tournaments'}
                >
                  Tournaments
                </StyledLink>
              </li>
            </>
          )}
        </NavList>

        {/* Right-aligned content */}
        <Box
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            paddingRight: '20px',
          }}
        >
          {isAuthenticated ? (
            <>
              {!isMobile && (
                <StyledLink
                  to="/profile"
                  state={{ nextPath: '/profile' }}
                  isActive={location.pathname === '/profile'}
                >
                  Profile
                </StyledLink>
              )}
              <ProfileAvatar
                onClick={handleOpenMenu}
                src={user?.profilePicture || undefined}
              >
                {!user?.profilePicture && getInitials()}
              </ProfileAvatar>
              <StyledMenu
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                onClick={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    navigate('/profile');
                  }}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">Profile</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    navigate('/settings');
                  }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">Settings</Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    handleSignOut();
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">Logout</Typography>
                </MenuItem>
              </StyledMenu>
            </>
          ) : (
            <StyledLink to="/login" isActive={location.pathname === '/login'}>
              Login
            </StyledLink>
          )}
        </Box>
      </StyledNav>

      <StyledDrawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawer}
      </StyledDrawer>
    </>
  );
};

export default Header;
